/**
 * XeroReconcileDialog.jsx — what the Jobs-tab refresh found, and what to do about it.
 *
 * Report first, write second. The sweep never changes a job on its own: a job
 * reaching 'paid' flips crew ledger rows pending → earned through
 * fireTriggersForJob, and only a super_admin can unearn them. So the dialog
 * shows the proposed moves with checkboxes and applies only what is ticked.
 *
 * Report-only proposals (the quoted → confirmed rule) render as information and
 * cannot be ticked at all. A job parked at 'quoted' during a re-quote is a
 * deliberate human decision, and an automatic sweep must not overwrite it.
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button,
  Checkbox, Chip, Divider, CircularProgress, Alert, Tooltip,
} from '@mui/material';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import { fmtC, formatStatus } from '../../lib/utils';

const SKIP_REASONS = {
  cooldown: 'Xero was checked less than 5 minutes ago — job statuses were still re-checked from local data.',
  rate_limited: 'Xero has capped this account for now — job statuses were still re-checked from local data.',
  rate_limited_midway: 'Xero capped this account partway through — some invoices were not checked.',
  not_connected: 'Xero is not connected. Job statuses were re-checked from local data only.',
  not_configured: 'Xero is not configured on this server.',
};

function Section({ title, count, children, tone }) {
  if (!count) return null;
  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="caption"
        sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
          color: tone === 'warning' ? 'warning.main' : 'text.secondary' }}
      >
        {title} ({count})
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

export default function XeroReconcileDialog({ open, onClose, result, loading, error, onApply, applying, applyResult }) {
  const [checked, setChecked] = useState({});

  const proposals = result?.jobs?.proposals || [];
  const actionable = proposals.filter((p) => !p.reportOnly);
  const reportOnly = proposals.filter((p) => p.reportOnly);

  // Default every actionable move to ticked — they are mechanical consequences
  // of the invoices, and the common case is accepting all of them.
  useEffect(() => {
    const fresh = (result?.jobs?.proposals || []).filter((p) => !p.reportOnly);
    setChecked(Object.fromEntries(fresh.map((p) => [p.jobId, true])));
  }, [result]);

  const selected = actionable.filter((p) => checked[p.jobId]);

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '1rem' }}>
        <CloudSyncOutlinedIcon sx={{ fontSize: 20 }} />
        Refresh from Xero
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Checking Xero and re-reading job statuses…
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ my: 1 }}>{error.message}</Alert>}

        {applyResult && (
          <Alert severity={applyResult.applied?.length ? 'success' : 'info'} sx={{ mb: 1 }}>
            {applyResult.applied?.length
              ? applyResult.applied.map((a) => `${a.title}: ${formatStatus(a.from)} → ${formatStatus(a.to)}`).join(' · ')
              : 'No job statuses changed.'}
            {applyResult.skipped?.length ? ` · ${applyResult.skipped.length} skipped` : ''}
          </Alert>
        )}

        {result && !loading && (
          <>
            <Typography variant="body2" color="text.secondary">
              {result.xero?.ran
                ? `Checked ${result.xero.scanned} of ${result.xero.eligibleTotal} open invoice${result.xero.eligibleTotal === 1 ? '' : 's'} in Xero · ${result.xero.updated} updated.`
                : (SKIP_REASONS[result.xero?.skipped] || 'Xero was not contacted this time.')}
              {result.xero?.truncated ? ' Press again in 5 minutes for the rest.' : ''}
            </Typography>

            {result.xero?.errors?.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {result.xero.errors.length} invoice{result.xero.errors.length === 1 ? '' : 's'} could not be checked:
                {' '}{result.xero.errors[0].number} — {result.xero.errors[0].message}
              </Alert>
            )}

            <Section title="Job statuses to update" count={actionable.length}>
              {actionable.map((p) => (
                <Box key={p.jobId} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.25 }}>
                  <Checkbox
                    size="small"
                    checked={!!checked[p.jobId]}
                    onChange={() => setChecked((c) => ({ ...c, [p.jobId]: !c[p.jobId] }))}
                    sx={{ p: 0.4 }}
                    inputProps={{ 'aria-label': `Apply ${p.title} ${p.from} to ${p.to}` }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {p.title}{' '}
                      <Chip size="small" label={`${formatStatus(p.from)} → ${formatStatus(p.to)}`}
                        sx={{ height: 17, fontSize: '0.62rem', ml: 0.5 }} />
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                      {p.reason}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Section>

            <Section title="Worth a look — not changed automatically" count={reportOnly.length}>
              {reportOnly.map((p) => (
                <Box key={p.jobId} sx={{ py: 0.4, pl: 0.5 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {p.title}{' '}
                    <Chip size="small" variant="outlined" label={`still ${formatStatus(p.from)}`}
                      sx={{ height: 17, fontSize: '0.62rem', ml: 0.5 }} />
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                    {p.reason}. Left alone in case the quote is being revised — change it by hand if not.
                  </Typography>
                </Box>
              ))}
            </Section>

            <Section title="Skipped — could not be worked out" count={result.jobs?.unresolved?.length} tone="warning">
              {(result.jobs?.unresolved || []).map((u) => (
                <Typography key={u.jobId} variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.68rem', pl: 0.5 }}>
                  {u.title} — {u.unresolved}
                </Typography>
              ))}
            </Section>

            <Section title="Invoices not in Xero" count={result.unsynced?.length} tone="warning">
              {(result.unsynced || []).map((u) => (
                <Typography key={u.id} variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.68rem', pl: 0.5 }}>
                  {u.number} · {u.client} · {fmtC(u.amount)} — {u.reason}
                </Typography>
              ))}
            </Section>

            <Section title="In Xero, missing from the portal" count={result.xeroOnly?.length} tone="warning">
              {(result.xeroOnly || []).map((x) => (
                <Typography key={x.xeroInvoiceId} variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.68rem', pl: 0.5 }}>
                  {x.date} · {x.number} · {x.contact} · {fmtC(x.total)}
                  {x.matchesRfosNumber ? ' — a portal invoice has this number but lost its link' : ''}
                </Typography>
              ))}
            </Section>

            {result.xeroOnlyError && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '0.68rem' }}>
                Could not list Xero invoices: {result.xeroOnlyError}
              </Typography>
            )}

            {proposals.length === 0 && !result.unsynced?.length && !result.xeroOnly?.length && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" color="text.secondary">
                  Everything matches. No job statuses are out of step with their invoices.
                </Typography>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={applying} sx={{ textTransform: 'none' }}>Close</Button>
        <Tooltip arrow title={selected.length ? '' : 'Nothing selected to apply'}>
          <span>
            <Button
              variant="contained"
              size="small"
              disabled={!selected.length || applying}
              onClick={() => onApply(selected.map((p) => ({ jobId: p.jobId, from: p.from, to: p.to })))}
              sx={{ textTransform: 'none' }}
            >
              {applying ? 'Applying…' : `Apply ${selected.length || ''}`.trim()}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
